import { useCallback, useState } from 'react';

const STORAGE_KEY = 'search.recentSearches';
const MAX_RECENT = 8;

const read = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * Recently searched card names, most recent first.
 *
 * Only committed searches are recorded, never raw keystrokes, so the list holds
 * things the user actually settled on rather than every prefix they typed.
 */
export const useRecentSearches = () => {
  const [recentSearches, setRecentSearches] = useState<string[]>(read);

  const addRecentSearch = useCallback((term: string) => {
    const value = term.trim();
    if (!value) return;

    setRecentSearches((prev) => {
      const deduped = prev.filter((v) => v.toLowerCase() !== value.toLowerCase());
      const next = [value, ...deduped].slice(0, MAX_RECENT);
      // No-op if this is already the head, so a re-render does not rewrite storage.
      if (next.length === prev.length && next[0] === prev[0]) return prev;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable (Safari private mode) — keep the in-memory list.
      }
      return next;
    });
  }, []);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return { recentSearches, addRecentSearch, clearRecentSearches };
};

export default useRecentSearches;
